import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../components/Card';
import CapabilitiesTable from '../components/CapabilitiesTable';
import { fetchRolesCatalog } from '../api/auth';
import {
  ALL_MODULES,
  ALL_SCOPES,
  MODULE_LABEL_KEYS,
  MODULE_SCOPES,
  SCOPE_LABEL_KEYS,
} from '../nav/entitlements';
import './SettingsAccessPage.css';

/* Paramètres › Utilisateur — LECTURE SEULE.
   La personne connectée voit son compte, le ou les rôles qu'elle porte sur le
   client actif, et la grille complète des rôles pour se situer. Toute
   modification vit dans Administration (admin plateforme). */

// Même ordre que la matrice d'Administration : les rôles hors liste passent après.
const ROLE_ORDER = ['admin', 'full-access', 'ceo', 'finance', 'hr', 'sales', 'viewer'];
function roleRank(slug) { const i = ROLE_ORDER.indexOf(slug); return i === -1 ? 99 : i; }

export default function SettingsAccessPage({
  authUser, roles = [], scopes = {}, activeTenant, visibleModules = [],
}) {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState([]);
  const [capabilities, setCapabilities] = useState([]);

  useEffect(() => {
    let alive = true;
    fetchRolesCatalog().then((res) => {
      if (!alive) return;
      if (res.data) setCatalog(res.data);
      if (res.capabilities) setCapabilities(res.capabilities);
    });
    return () => { alive = false; };
  }, []);

  const mine = new Set(roles);
  const ordered = [...catalog].sort((a, b) => roleRank(a.slug) - roleRank(b.slug));
  const enabled = new Set(activeTenant?.enabled_modules || []);
  const visible = new Set(visibleModules);

  function levelLabel(level) {
    if (level === 'rw') return t('settings:access.levels.readWrite');
    if (level === 'r') return t('settings:access.levels.read');
    return t('settings:access.levels.none');
  }

  return (
    <div className="sa-page">
      <Card
        title={t('settings:access.identityTitle')}
        subtitle={activeTenant?.name || undefined}
      >
        <dl className="sa-identity">
          <div>
            <dt>{t('settings:access.name')}</dt>
            <dd>{authUser?.name || '—'}</dd>
          </div>
          <div>
            <dt>{t('settings:access.email')}</dt>
            <dd>{authUser?.email || '—'}</dd>
          </div>
          <div>
            <dt>{t('settings:access.myRoles')}</dt>
            <dd>
              {ordered.filter((r) => mine.has(r.slug)).map((r) => r.name).join(' · ')
                || [...mine].join(' · ') || '—'}
            </dd>
          </div>
        </dl>
      </Card>

      <Card
        title={t('settings:access.myAccessTitle')}
        flush
      >
        <table className="sa-table">
          <thead>
            <tr>
              {ALL_SCOPES.map((s) => <th key={s}>{t(SCOPE_LABEL_KEYS[s]) || s}</th>)}
            </tr>
          </thead>
          <tbody>
            <tr>
              {ALL_SCOPES.map((s) => (
                <td key={s} className={scopes[s] ? '' : 'sa-none'}>
                  {levelLabel(scopes[s])}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </Card>

      <Card
        title={t('settings:access.modulesTitle')}
        subtitle={t('settings:access.modulesSubtitle')}
        flush
      >
        <table className="sa-table">
          <thead>
            <tr>
              <th>{t('settings:access.moduleColumn')}</th>
              <th>{t('settings:access.soldColumn')}</th>
              <th>{t('settings:access.accessColumn')}</th>
              <th>{t('settings:access.usesColumn')}</th>
            </tr>
          </thead>
          <tbody>
            {ALL_MODULES.map((mid) => {
              const isOn = enabled.has(mid);
              const isVisible = visible.has(mid);
              return (
                <tr key={mid} className={isVisible ? 'lk-hi' : undefined}>
                  <td className="sa-role">
                    <span className="sa-role__name">{t(MODULE_LABEL_KEYS[mid]) || mid}</span>
                  </td>
                  <td className={isOn ? '' : 'sa-none'}>
                    {isOn ? t('settings:access.enabled') : t('settings:access.notEnabled')}
                  </td>
                  <td className={isVisible ? '' : 'sa-none'}>
                    {isVisible
                      ? t('settings:access.visible')
                      : (isOn ? t('settings:access.noRight') : '—')}
                  </td>
                  <td className="sa-none">
                    {(MODULE_SCOPES[mid] || [])
                      .map((s) => t(SCOPE_LABEL_KEYS[s]) || s)
                      .join(' · ')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card
        title={t('settings:access.allRolesTitle')}
        subtitle={t('settings:access.allRolesSubtitle')}
        flush
      >
        <table className="sa-table">
          <thead>
            <tr>
              <th>{t('settings:access.roleColumn')}</th>
              {ALL_SCOPES.map((s) => <th key={s}>{t(SCOPE_LABEL_KEYS[s]) || s}</th>)}
            </tr>
          </thead>
          <tbody>
            {ordered.map((role) => (
              <tr key={role.id} className={mine.has(role.slug) ? 'lk-hi' : undefined}>
                <td className="sa-role" title={role.description || undefined}>
                  <span className="sa-role__name">{role.name}</span>
                  {mine.has(role.slug) && (
                    <span className="sa-badge">{t('settings:access.mineBadge')}</span>
                  )}
                </td>
                {ALL_SCOPES.map((s) => {
                  const lvl = (role.scopes || {})[s] || '';
                  return (
                    <td key={s} className={lvl ? '' : 'sa-none'}>{levelLabel(lvl)}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <CapabilitiesTable
        capabilities={capabilities}
        roles={ordered}
        mine={roles}
      />
    </div>
  );
}
